import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

// Platform-wide default favicon (set by super admin); tenant favicon overrides it.
const favState = { platform: null, tenant: null };
export function setFavicon(url) {
  let link = document.querySelector("link[rel~='icon']");
  if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
  link.href = url;
}
const refreshFavicon = () => setFavicon(favState.tenant || favState.platform || "/api/app/icon.png?size=64");
const setHeadLink = (rel, href) => {
  let link = document.querySelector(`link[rel='${rel}']`);
  if (!link) { link = document.createElement("link"); link.rel = rel; document.head.appendChild(link); }
  if (link.getAttribute("href") !== href) link.href = href;
};
export function setTenantAppIcons(tenantId, version = "") {
  const q = tenantId ? `tenant=${tenantId}&v=${encodeURIComponent(version)}` : "";
  setHeadLink("manifest", `/api/app/manifest.json${q ? `?${q}` : ""}`);
  setHeadLink("apple-touch-icon", `/api/app/icon.png?size=180${q ? `&${q}` : ""}`);
}
export function setPlatformFavicon(url) { favState.platform = url || null; refreshFavicon(); }
api.get("/public/platform-branding").then(({ data }) => setPlatformFavicon(data?.favicon_url)).catch(() => {});

const TenantThemeContext = createContext(null);

// Convert hex (#rrggbb) to "h s% l%" string for Tailwind HSL vars
function hexToHsl(hex) {
  if (!hex || !hex.startsWith("#")) return "0 0% 50%";
  const clean = hex.replace("#", "");
  const r = parseInt(clean.slice(0, 2), 16) / 255;
  const g = parseInt(clean.slice(2, 4), 16) / 255;
  const b = parseInt(clean.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

const ROUNDING_MAP = {
  none: "0px",
  small: "6px",
  medium: "12px",
  large: "20px",
};

const GOOGLE_FONTS = [
  // Lodgy brand heading (licensed font; falls back to Montserrat)
  { name: "Proxima Nova", category: "sans" },
  // Serif (heading)
  { name: "Marcellus", category: "serif" },
  { name: "Playfair Display", category: "serif" },
  { name: "Cormorant Garamond", category: "serif" },
  { name: "Libre Caslon Text", category: "serif" },
  { name: "Libre Baskerville", category: "serif" },
  { name: "DM Serif Display", category: "serif" },
  { name: "Cinzel", category: "serif" },
  { name: "EB Garamond", category: "serif" },
  { name: "Lora", category: "serif" },
  { name: "Merriweather", category: "serif" },
  { name: "PT Serif", category: "serif" },
  { name: "Bitter", category: "serif" },
  { name: "Crimson Text", category: "serif" },
  { name: "Cardo", category: "serif" },
  { name: "Frank Ruhl Libre", category: "serif" },
  { name: "Spectral", category: "serif" },
  { name: "Tinos", category: "serif" },
  { name: "Italiana", category: "serif" },
  { name: "Trajan Pro", category: "serif" },
  { name: "Cormorant", category: "serif" },
  // Display / decorative for heading
  { name: "Cormorant Infant", category: "serif" },
  { name: "Forum", category: "serif" },
  { name: "Prata", category: "serif" },
  // Sans (body)
  { name: "Nunito", category: "sans" },
  { name: "Inter", category: "sans" },
  { name: "Manrope", category: "sans" },
  { name: "Poppins", category: "sans" },
  { name: "Roboto", category: "sans" },
  { name: "Lato", category: "sans" },
  { name: "Open Sans", category: "sans" },
  { name: "Montserrat", category: "sans" },
  { name: "Figtree", category: "sans" },
  { name: "Work Sans", category: "sans" },
  { name: "Source Sans 3", category: "sans" },
  { name: "Karla", category: "sans" },
  { name: "Quicksand", category: "sans" },
  { name: "DM Sans", category: "sans" },
  { name: "Plus Jakarta Sans", category: "sans" },
  { name: "Outfit", category: "sans" },
  { name: "Public Sans", category: "sans" },
  { name: "Be Vietnam Pro", category: "sans" },
  { name: "Hanken Grotesk", category: "sans" },
  { name: "Space Grotesk", category: "sans" },
  { name: "Rubik", category: "sans" },
  { name: "Fira Sans", category: "sans" },
];

export const HEADING_FONTS = GOOGLE_FONTS;
export const BODY_FONTS = GOOGLE_FONTS.filter((f) => f.category === "sans");
export const ROUNDING_OPTIONS = [
  { value: "none", label: "Bez zaobljenja", radius: "0px" },
  { value: "small", label: "Malo zaobljenje", radius: "6px" },
  { value: "medium", label: "Srednje zaobljenje", radius: "12px" },
  { value: "large", label: "Veliko zaobljenje", radius: "20px" },
];

function ensureFontLoaded(fontName) {
  if (!fontName || fontName === "Proxima Nova") return;
  const id = `gfont-${fontName.replace(/\s+/g, "-")}`;
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}:wght@300;400;500;600;700;800&display=swap`;
  document.head.appendChild(link);
}

const DARK_SKIP = new Set([
  "--background", "--foreground", "--card", "--card-foreground", "--popover", "--popover-foreground",
  "--secondary", "--secondary-foreground", "--muted", "--muted-foreground", "--border", "--input", "--surface",
  "--accent", "--accent-foreground", "--primary-soft",
  "--rw-bg-page", "--rw-bg-light", "--rw-bg-white", "--rw-border", "--rw-border-light",
  "--rw-text-primary", "--rw-text-secondary", "--rw-text-muted", "--rw-primary-light",
]);

export function applyTheme(t) {
  if (!t) return;
  const root = document.documentElement;

  // Load Google Fonts dynamically
  ensureFontLoaded(t.heading_font);
  ensureFontLoaded(t.body_font);

  // Map tenant theme → CSS variables for the whole system
  const mappings = {
    // Tailwind-compatible HSL vars
    "--background": hexToHsl(t.bg_page),
    "--foreground": hexToHsl(t.text_primary),
    "--card": hexToHsl(t.bg_white),
    "--card-foreground": hexToHsl(t.text_primary),
    "--popover": hexToHsl(t.bg_white),
    "--popover-foreground": hexToHsl(t.text_primary),
    "--primary": hexToHsl(t.primary_color),
    "--primary-foreground": hexToHsl(t.text_white),
    "--primary-soft": hexToHsl(t.primary_light),
    "--secondary": hexToHsl(t.bg_light),
    "--secondary-foreground": hexToHsl(t.text_secondary || t.text_primary),
    "--muted": hexToHsl(t.bg_light),
    "--muted-foreground": hexToHsl(t.text_muted),
    "--accent": hexToHsl(t.accent_color),
    "--accent-foreground": hexToHsl(t.text_white),
    "--border": hexToHsl(t.border_color),
    "--input": hexToHsl(t.border_color),
    "--ring": hexToHsl(t.primary_color),
    "--surface": hexToHsl(t.bg_white),

    // Raw hex values for direct CSS use
    "--rw-primary": t.primary_color,
    "--rw-primary-hover": t.primary_hover,
    "--rw-primary-dark": t.primary_dark,
    "--rw-primary-light": t.primary_light,
    "--rw-dark": t.dark_color,
    "--rw-dark-hover": t.dark_hover,
    "--rw-bg-page": t.bg_page,
    "--rw-bg-light": t.bg_light,
    "--rw-bg-white": t.bg_white,
    "--rw-border": t.border_color,
    "--rw-border-light": t.border_light,
    "--rw-text-primary": t.text_primary,
    "--rw-text-secondary": t.text_secondary,
    "--rw-text-muted": t.text_muted,
    "--rw-text-white": t.text_white,
    "--rw-calendar": t.calendar_color,

    // Fonts
    "--rw-font-heading": `"${t.heading_font}", "Montserrat", "Inter", ${(GOOGLE_FONTS.find((f) => f.name === t.heading_font) || {}).category === "serif" ? "serif" : "sans-serif"}`,
    "--rw-font-body": `"${t.body_font}", system-ui, sans-serif`,
    "--rw-font-heading-weight": t.heading_font_weight || "600",

    // Rounding
    "--radius": ROUNDING_MAP[t.rounding] || "6px",
    "--rw-radius": ROUNDING_MAP[t.rounding] || "6px",
  };

  // Dark mode: neutral surfaces/text come from the shared Lodgy dark palette (.dark in index.css);
  // only brand colours, fonts and rounding follow the tenant theme.
  const dark = root.classList.contains("dark");
  if (dark) {
    // A navy/very dark brand primary would vanish on dark surfaces — lift its lightness.
    const lift = (hsl) => {
      const [h, sat, l] = (hsl || "").split(" ").map((x) => parseFloat(x));
      if (Number.isNaN(l)) return hsl;
      return l < 42 ? `${h} ${Math.max(sat, 55)}% ${l < 20 ? 58 : 46}%` : hsl;
    };
    mappings["--primary"] = lift(mappings["--primary"]);
    mappings["--ring"] = mappings["--primary"];
    mappings["--primary-foreground"] = "0 0% 100%";
  }
  Object.entries(mappings).forEach(([k, v]) => {
    if (dark && DARK_SKIP.has(k)) root.style.removeProperty(k);
    else if (v != null) root.style.setProperty(k, v);
  });

  // Apply body font
  document.body.style.fontFamily = `var(--rw-font-body)`;

  // Sidebar = Dark Color
  if (t.dark_color) root.style.setProperty("--sidebar", hexToHsl(t.dark_color));

  // Favicon
  favState.tenant = t.favicon_url || null;
  refreshFavicon();
  if (t.id) setTenantAppIcons(t.id, t.favicon_url || "");
}

export const FONT_SIZE_KEY = "rw-font-size";
export function applyFontSize(v) {
  const val = v || localStorage.getItem(FONT_SIZE_KEY) || "normal";
  document.documentElement.setAttribute("data-font-size", val);
}
try { applyFontSize(); } catch { /* ignore */ }

export function TenantThemeProvider({ children }) {
  const { tenant } = useAuth();
  const [override, setOverride] = useState(null); // ad-hoc preview without saving

  const effective = override || tenant;

  useEffect(() => {
    if (!effective) return undefined;
    applyTheme(effective);
    const re = () => applyTheme(effective);
    window.addEventListener("rw-theme-change", re);
    return () => window.removeEventListener("rw-theme-change", re);
  }, [effective]);

  const preview = useCallback((partial) => {
    setOverride((cur) => ({ ...(cur || tenant || {}), ...partial }));
  }, [tenant]);

  const clearPreview = useCallback(() => setOverride(null), []);

  return (
    <TenantThemeContext.Provider value={{ tenant: effective, preview, clearPreview }}>
      {children}
    </TenantThemeContext.Provider>
  );
}

export function useTenantTheme() {
  const ctx = useContext(TenantThemeContext);
  if (!ctx) throw new Error("useTenantTheme must be used inside TenantThemeProvider");
  return ctx;
}
